import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatErr, IMAGES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { login, register, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      isLogin ? await login(form.email, form.password) : await register(form.name, form.email, form.password);
      const next = params.get("next") || "/dashboard";
      if (params.get("save") && next.startsWith("/quote/")) await api.post(`/quotes/${next.split("/")[2]}/save`).catch(() => {});
      nav(next);
    } catch (ex) {
      setErr(formatErr(ex));
      setLoading(false);
    }
  };

  const qs = params.toString() ? `?${params.toString()}` : "";
  return (
    <section className="grid lg:grid-cols-2 min-h-[calc(100vh-72px)]" data-testid={`${mode}-page`}>
      <div className="hidden lg:block relative">
        <img src={IMAGES.bedroom2} alt="homiin interior" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-brand-ink/30" />
        <p className="absolute bottom-12 left-12 right-12 font-serif-display text-4xl text-brand-cream leading-tight">Every quotation, every revision — kept safe in one place.</p>
      </div>
      <div className="flex items-center justify-center px-6 py-16">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="auth-form">
          <h1 className="font-serif-display text-5xl text-brand-ink">{isLogin ? "Welcome back" : "Create account"}</h1>
          <p className="text-sm text-brand-muted mt-3">{isLogin ? "Sign in to view your saved quotations." : "Save quotations and track your project."}</p>
          <button type="button" onClick={() => loginWithGoogle(params.get("next"), params.get("save"))} data-testid="google-signin-btn"
            className="mt-10 w-full rounded-full border border-brand-line bg-white hover:border-brand-accent py-3.5 text-sm text-brand-ink transition-colors inline-flex justify-center items-center gap-3">
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.7 1.2 9.2 3.6l6.9-6.9C35.9 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l8 6.2C12.5 13.6 17.8 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z"/><path fill="#FBBC05" d="M10.6 28.6A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.1.8-4.6l-8-6.2A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l8-6.2z"/><path fill="#34A853" d="M24 48c6.3 0 11.7-2.1 15.6-5.7l-7.5-5.8c-2.1 1.4-4.8 2.3-8.1 2.3-6.2 0-11.5-4.1-13.4-9.8l-8 6.2C6.5 42.6 14.6 48 24 48z"/></svg>
            Continue with Google
          </button>
          <div className="flex items-center gap-4 mt-6 text-xs text-brand-muted-light"><span className="flex-1 h-px bg-brand-line" />or with email<span className="flex-1 h-px bg-brand-line" /></div>
          <div className="space-y-5 mt-6">
            {!isLogin && (
              <div className="space-y-2"><Label htmlFor="name">Full name</Label>
                <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12 bg-brand-cream" data-testid="auth-name-input" /></div>
            )}
            <div className="space-y-2"><Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12 bg-brand-cream" data-testid="auth-email-input" /></div>
            <div className="space-y-2"><Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-12 bg-brand-cream" data-testid="auth-password-input" /></div>
          </div>
          {err && <p className="text-sm text-red-700 mt-4" data-testid="auth-error">{err}</p>}
          <button disabled={loading} className="mt-8 w-full rounded-full bg-brand-ink hover:bg-brand-accent text-brand-cream py-4 text-sm transition-colors inline-flex justify-center items-center gap-2" data-testid="auth-submit-btn">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} {isLogin ? "Sign in" : "Create account"}
          </button>
          <p className="text-sm text-brand-muted mt-6 text-center">
            {isLogin ? "New to homiin? " : "Already have an account? "}
            <Link to={`${isLogin ? "/register" : "/login"}${qs}`} className="text-brand-accent hover:text-brand-accent-dark" data-testid="auth-switch-link">{isLogin ? "Create an account" : "Sign in"}</Link>
          </p>
        </form>
      </div>
    </section>
  );
}
